import React, { useEffect, useRef, useState } from 'react';

interface ContextMenuOption {
    label: string;
    onClick: () => void;
    icon?: string | React.ReactNode;
    className?: string;
    iconClassName?: string;
}

interface ContextMenuProps {
    x: number;
    y: number;
    options: ContextMenuOption[];
    onClose: () => void;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, options, onClose }) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [adjustedY, setAdjustedY] = useState(y);

    useEffect(() => {
        if (menuRef.current) {
            const menuHeight = menuRef.current.offsetHeight;
            const windowHeight = window.innerHeight;
            const padding = 10; // Padding from bottom of screen

            // Check if menu goes off the bottom
            if (y + menuHeight + padding > windowHeight) {
                // Reposition menu above the click point
                const newY = Math.max(padding, y - menuHeight - padding);
                setAdjustedY(newY);
            } else {
                setAdjustedY(y);
            }
        }
    }, [y]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    return (
        <div
            ref={menuRef}
            className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 min-w-[180px]"
            style={{
                left: `${x}px`,
                top: `${adjustedY}px`,
            }}
        >
            {options.map((option, index) => (
                <button
                    data-ai-id="context-menu-item"
                    key={index}
                    onClick={() => {
                        option.onClick();
                        onClose();
                    }}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-2 border-none bg-transparent cursor-pointer ${option.className || 'text-gray-700 hover:bg-teal-50 hover:text-teal-700'
                        }`}
                >
                    {option.icon && <span className={option.iconClassName}>{option.icon}</span>}
                    <span>{option.label}</span>
                </button>
            ))}
        </div>
    );
};
